import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { revenirOuRemplacer, usePath } from '../router.js'
import { useToast } from '../admin/primitives.js'
import { motionLevel } from './auth-motion.js'
import { pushOverlay } from './overlays.js'
import { arriverSansSceau, attendreFocus, sceauTermine, setSceauPlayer, type Sceau } from './sceau-bus.js'
import {
  departMontee, lireJetons, momentDeLevee, niveauSceau, sequenceSceau, vecteurVol, volPossible,
  type JetonsSceau, type SequenceSceau,
} from './sceau-timeline.js'
import './sceau.css'

/**
 * LE SCEAU DE FINALISATION — la mise en scène (11/10/2026).
 *
 * Un geste, un glyphe, une couleur. Après la réponse 2xx d'une finalisation
 * (la vérification renforcée, quand elle était due, est déjà passée), un
 * voile opaque couvre la page ; le ✓ de la pastille « Clôturée » sort du
 * bouton, se signe — le cercle, puis la coche —, frappe une fois ; la page
 * change DESSOUS ; le voile se lève et le sceau vole se poser sur la pastille
 * de la page d'arrivée, au pixel près.
 *
 * ═══ POURQUOI ICI ═══
 * Tout ce que l'écran de départ rend meurt au changement de page (`.lc-route`
 * est remontée). L'hôte vit à côté des toasts, dans AppShell : il est le seul
 * à pouvoir traverser la redirection. Il ne sait rien de la Comptabilité :
 * textes, destination et sélecteurs lui sont fournis (sceau-bus.ts).
 *
 * ═══ CE QUI NE PEUT PAS ARRIVER ═══
 *  · Le produit retenu : tout est minuté (setTimeout), rien n'attend la fin
 *    d'une animation — un onglet caché les gèle. Un filet rend tout.
 *  · Un geste détourné : un changement d'adresse avant la bascule abandonne
 *    le sceau sans rediriger ; après, une autre adresse que la destination
 *    l'abandonne sans voler le focus.
 *  · Une panne qui bloque : toute exception retombe sur l'arrivée sans sceau,
 *    et le toast part quand même.
 *  · Une information portée par le seul mouvement : le sceau est
 *    `aria-hidden` ; le toast (role=status) et la pastille de la page disent
 *    la même chose.
 */

type Phase = 'joue' | 'leve' | 'passe'

interface Scene {
  s: Sceau
  niveau: 'full' | 'reduced'
  phase: Phase
  sortie: 'vol' | 'fondu' | null
  depart: { dx: number; dy: number; s0: number }
  vol: { vx: number; vy: number; vs: number } | null
}

interface Course {
  s: Sceau
  seq: SequenceSceau
  jetons: JetonsSceau
  t0: number
  phase: Phase
  minuteurs: number[]
  basculee: boolean
  annoncee: boolean
  finie: boolean
  cheminDepart: string
  shell: HTMLElement | null
  depile: (() => void) | null
  classeHtml: string | null
  clavier: (e: KeyboardEvent) => void
  visibilite: () => void
}

export function SceauHost({ scrollRef }: { scrollRef: RefObject<HTMLDivElement | null> }) {
  const toast = useToast()
  const path = usePath()
  const [scene, setScene] = useState<Scene | null>(null)
  const glyphe = useRef<SVGSVGElement>(null)
  const course = useRef<Course | null>(null)
  const toastRef = useRef(toast)
  toastRef.current = toast
  const cheminRef = useRef(path)
  cheminRef.current = path

  /* ─── les gestes élémentaires, sans état React propre ─── */

  const plusTard = (c: Course, ms: number, fn: () => void): void => {
    const id = window.setTimeout(() => {
      try { fn() } catch { secours(c) }
    }, Math.max(0, ms))
    c.minuteurs.push(id)
  }

  const arreterMinuteurs = (c: Course): void => {
    for (const id of c.minuteurs) window.clearTimeout(id)
    c.minuteurs = []
  }

  const annoncer = (c: Course): void => {
    if (c.annoncee) return
    c.annoncee = true
    toastRef.current('ok', c.s.annonce)
  }

  /** Rend la main : plus d'`inert`, plus d'overlay, plus d'écoute clavier. */
  const liberer = (c: Course): void => {
    if (c.shell) c.shell.inert = false
    c.shell = null
    c.depile?.()
    c.depile = null
    window.removeEventListener('keydown', c.clavier, true)
    document.removeEventListener('visibilitychange', c.visibilite)
  }

  const retirerClasse = (c: Course): void => {
    if (c.classeHtml) document.documentElement.classList.remove(c.classeHtml)
    c.classeHtml = null
  }

  /**
   * LA BASCULE — sous le voile opaque : en haut, puis la destination. Si l'on
   * venait d'elle, on y revient (un seul exemplaire dans l'historique) ;
   * sinon l'adresse de départ est remplacée (`revenirOuRemplacer`).
   */
  const basculer = (c: Course): void => {
    if (c.basculee) return
    c.basculee = true
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    revenirOuRemplacer(c.s.destination)
  }

  /** Fin de course : tout est rendu, le sceau démonté, un autre peut se jouer. */
  const finir = (c: Course): void => {
    if (c.finie) return
    c.finie = true
    arreterMinuteurs(c)
    liberer(c)
    retirerClasse(c)
    annoncer(c)
    if (course.current === c) {
      course.current = null
      setScene(null)
      sceauTermine()
    }
  }

  /** Une exception : l'arrivée sans sceau, et le toast quand même. */
  const secours = (c: Course): void => {
    try {
      const avait = c.basculee
      c.basculee = true
      finir(c)
      if (!avait) arriverSansSceau(c.s.destination, c.s.focus)
    } catch { /* plus rien à rendre */ }
  }

  const sonder = (c: Course): void => {
    if (c.finie || c.phase !== 'joue') return
    const t = performance.now() - c.t0
    const pret = document.querySelector(c.s.pret) !== null
      && (c.s.attendre === null || document.querySelector(c.s.attendre) !== null)
    if (pret) { plusTard(c, momentDeLevee(c.seq, t) - t, () => lever(c)); return }
    if (t >= c.seq.leveeMax) { lever(c); return }
    plusTard(c, Math.min(60, c.seq.leveeMax - t), () => sonder(c))
  }

  /** LA LEVÉE — la page est utilisable dès cet instant. */
  const lever = (c: Course): void => {
    if (c.finie || c.phase !== 'joue') return
    c.phase = 'leve'
    liberer(c)
    if (c.seq.vol) {
      const g = glyphe.current?.getBoundingClientRect() ?? null
      const ancre = document.querySelector(c.s.ancre)?.getBoundingClientRect() ?? null
      const vue = { largeur: window.innerWidth, hauteur: window.innerHeight }
      if (g && ancre && volPossible(g, ancre, vue)) {
        const vol = vecteurVol(g, ancre)
        setScene((sc) => (sc ? { ...sc, phase: 'leve', sortie: 'vol', vol } : sc))
        const classe = c.s.classeArrivee
        if (classe) {
          plusTard(c, c.seq.arrivee, () => {
            if (window.location.pathname !== c.s.destination) return
            document.documentElement.classList.add(classe)
            c.classeHtml = classe
          })
        }
      } else {
        setScene((sc) => (sc ? { ...sc, phase: 'leve', sortie: 'fondu' } : sc))
      }
    } else {
      // Mouvement réduit : tout s'efface sur place, sans voyage.
      setScene((sc) => (sc ? { ...sc, phase: 'passe' } : sc))
    }
    annoncer(c)
    plusTard(c, c.seq.demontage, () => {
      setScene(null)
      attendreFocus(c.s.focus, c.s.destination)
    })
    plusTard(c, Math.max(c.seq.demontage, c.seq.nettoyage), () => finir(c))
  }

  /** PASSER — un clic, Échap, Entrée, Espace, ou l'onglet qui se cache. Rien n'est défait : la nuit est déjà finalisée. */
  const passer = (c: Course): void => {
    if (c.finie || c.phase !== 'joue') return
    c.phase = 'passe'
    arreterMinuteurs(c)
    basculer(c)
    liberer(c)
    annoncer(c)
    setScene((sc) => (sc ? { ...sc, phase: 'passe' } : sc))
    plusTard(c, c.jetons.instant, () => {
      finir(c)
      attendreFocus(c.s.focus, c.s.destination)
    })
  }

  /* ─── le lecteur, enregistré une fois ─── */

  useEffect(() => {
    const jouer = (s: Sceau): boolean => {
      if (course.current) return false
      try {
        const niveau = niveauSceau(motionLevel(), window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        const jetons = lireJetons(document.documentElement)
        const seq = sequenceSceau(niveau, document.hidden, jetons)
        if (!seq.scene || niveau === 'minimal') {
          // Minimal, ou onglet caché : l'arrivée seule, le toast tout de suite.
          toastRef.current('ok', s.annonce)
          arriverSansSceau(s.destination, s.focus)
          sceauTermine()
          return true
        }
        const shell = document.querySelector('.lc-shell2')
        const c: Course = {
          s, seq, jetons,
          t0: performance.now(),
          phase: 'joue',
          minuteurs: [],
          basculee: false, annoncee: false, finie: false,
          cheminDepart: cheminRef.current,
          shell: shell instanceof HTMLElement ? shell : null,
          depile: null,
          classeHtml: null,
          clavier: (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); passer(c) }
          },
          visibilite: () => { if (document.hidden) passer(c) },
        }
        course.current = c
        // Le moment ne se double pas : la page dessous ne reçoit plus rien.
        if (c.shell) c.shell.inert = true
        c.depile = pushOverlay(() => passer(c))
        window.addEventListener('keydown', c.clavier, true)
        document.addEventListener('visibilitychange', c.visibilite)
        setScene({
          s, niveau, phase: 'joue', sortie: null, vol: null,
          depart: seq.vol
            ? departMontee(s.origine, { largeur: window.innerWidth, hauteur: window.innerHeight })
            : { dx: 0, dy: 0, s0: 1 },
        })
        plusTard(c, seq.bascule, () => { basculer(c); sonder(c) })
        // LE FILET — quoi qu'il arrive, le produit est rendu.
        plusTard(c, seq.filet, () => {
          basculer(c)
          finir(c)
          attendreFocus(s.focus, s.destination)
        })
        return true
      } catch {
        const c = course.current
        if (c) secours(c)
        else {
          toastRef.current('ok', s.annonce)
          arriverSansSceau(s.destination, s.focus)
          sceauTermine()
        }
        return true
      }
    }
    setSceauPlayer(jouer)
    return () => {
      setSceauPlayer(null)
      const c = course.current
      if (c) {
        arreterMinuteurs(c)
        liberer(c)
        retirerClasse(c)
        c.finie = true
        course.current = null
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── jamais détourné : l'adresse qui change abandonne le sceau ─── */
  useEffect(() => {
    const c = course.current
    if (!c || c.finie) return
    if (!c.basculee ? path !== c.cheminDepart : path !== c.s.destination) finir(c)
  }, [path]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!scene || typeof document === 'undefined') return null

  const { s, depart, vol, phase, sortie, niveau } = scene
  const style: Record<string, string> = {
    '--sceau-dx': `${depart.dx}px`,
    '--sceau-dy': `${depart.dy}px`,
    '--sceau-s0': String(depart.s0),
  }
  if (vol) {
    style['--sceau-vx'] = `${vol.vx}px`
    style['--sceau-vy'] = `${vol.vy}px`
    style['--sceau-vs'] = String(vol.vs)
  }
  if (s.teinte) style['--sceau-teinte'] = s.teinte
  const classes = [
    'lc-sceau',
    niveau === 'reduced' ? 'lc-sceau--reduit' : '',
    phase === 'leve' ? 'lc-sceau--leve' : '',
    phase === 'passe' ? 'lc-sceau--passe' : '',
    sortie === 'vol' ? 'lc-sceau--vol' : '',
    sortie === 'fondu' ? 'lc-sceau--fondu' : '',
  ].filter(Boolean).join(' ')

  return createPortal(
    <div
      className={classes}
      aria-hidden="true"
      style={style as CSSProperties}
      onClick={() => { const c = course.current; if (c) passer(c) }}
    >
      <div className="lc-sceau__voile" />
      <div className="lc-sceau__halo" />
      <div className="lc-sceau__onde" />
      <div className="lc-sceau__onde2" />
      <div className="lc-sceau__scene">
        <div className="lc-sceau__vol">
          <div className="lc-sceau__monte">
            <div className="lc-sceau__frappe">
              {/* La géométrie exacte du CheckCircle2 de lucide : le sceau devient la pastille. */}
              <svg
                ref={glyphe}
                className="lc-sceau__glyphe"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                focusable="false"
              >
                <circle className="lc-sceau__sillon" cx="12" cy="12" r="10" />
                <circle className="lc-sceau__cercle" cx="12" cy="12" r="10" pathLength={1} />
                <path className="lc-sceau__coche" d="m9 12 2 2 4-4" pathLength={1} />
              </svg>
            </div>
          </div>
        </div>
        <div className="lc-sceau__mots">
          <p className="lc-sceau__titre">{s.titre}</p>
          <p className="lc-sceau__sous">{s.sousTitre}</p>
          {s.puce ? <span className="lc-sceau__puce lc-pill lc-pill--idle">{s.puce}</span> : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}
